/**
 * TaskBoardHeader Component
 *
 * Restructured header with clear visual hierarchy:
 * - Scope as prominent heading (not dropdown)
 * - Context line showing task count
 * - Compact search (icon expands)
 * - Utilities grouped in menu
 * - Reduced primary action size
 *
 * Hierarchy: Scope Title > Context Line > Primary Action > Utilities
 */

import React from 'react';
import PropTypes from 'prop-types';
import { Input, Button, Tooltip, Dropdown, Menu, Badge } from 'antd';
import {
  SearchOutlined,
  PlusOutlined,
  UploadOutlined,
  DownloadOutlined,
  UserOutlined,
  TeamOutlined,
  FilterOutlined,
  SettingOutlined,
} from '@ant-design/icons';
import { useTaskExport } from '../../hooks/useTaskExport';
import { space, text } from '../../configs/designTokens';
import '../../styles/index.scss';

const TaskBoardHeader = ({
  windowWidth,
  showMyTasks,
  setShowMyTasks,
  searchValue,
  onSearchChange,
  onSearchClear,
  onBulkImportClick,
  onCreateTaskClick,
  level,
  setViewChanged,
  // Filter integration
  showFilters,
  setShowFilters,
  activeFilterCount,
  // Context data
  taskCount = 0,
  overdueCount = 0,
  // Export
  getExportWhere,
}) => {
  const isMobile = windowWidth < 768;
  const { exportTasks, isExporting } = useTaskExport();

  const handleExport = () => {
    const where = getExportWhere ? getExportWhere() : undefined;
    exportTasks(where);
  };

  const handleScopeToggle = () => {
    setShowMyTasks(!showMyTasks);
    setViewChanged(true);
  };

  // Utilities menu (Filter, Import, Export)
  const utilitiesMenu = (
    <Menu className="task-board-utilities-menu">
      <Menu.Item
        key="filter"
        icon={<FilterOutlined />}
        onClick={() => setShowFilters(!showFilters)}
      >
        {showFilters ? 'Hide Filters' : 'Show Filters'}
        {activeFilterCount > 0 && (
          <Badge count={activeFilterCount} size="small" style={{ marginLeft: space[2] }} />
        )}
      </Menu.Item>
      {level === 'PLATFORM' && (
        <>
          <Menu.Item key="import" icon={<UploadOutlined />} onClick={onBulkImportClick}>
            Bulk Import
          </Menu.Item>
          <Menu.Item
            key="export"
            icon={<DownloadOutlined />}
            onClick={handleExport}
            disabled={isExporting}
          >
            {isExporting ? 'Exporting...' : 'Export Tasks'}
          </Menu.Item>
        </>
      )}
    </Menu>
  );

  // Build context line
  const contextParts = [];
  if (taskCount > 0) {
    contextParts.push(`${taskCount} task${taskCount !== 1 ? 's' : ''}`);
  }
  if (overdueCount > 0) {
    contextParts.push(`${overdueCount} overdue`);
  }
  const contextLine = contextParts.join(' · ');

  return (
    <div className="task-board-header-modern">
      {/* Mobile Layout */}
      {isMobile ? (
        <div className="task-board-header__mobile">
          {/* Top row: Title + Actions */}
          <div className="task-board-header__mobile-top">
            <div className="task-board-header__title-group">
              <h1 className="task-board-header__title">
                {showMyTasks ? 'My Tasks' : 'All Tasks'}
              </h1>
              {contextLine && (
                <span className="task-board-header__context">{contextLine}</span>
              )}
            </div>
            <div className="task-board-header__mobile-actions">
              <Button
                type="primary"
                icon={<PlusOutlined />}
                className="task-board-header__add-btn"
                onClick={onCreateTaskClick}
              />
            </div>
          </div>

          {/* Search row */}
          <div className="task-board-header__mobile-search-row">
            <Input
              className="task-board-search-mobile"
              placeholder="Search tasks..."
              value={searchValue}
              onChange={(e) => onSearchChange(e.target.value)}
              prefix={<SearchOutlined style={{ color: text.muted }} />}
              allowClear
              onClear={onSearchClear}
            />
            <button
              className={`task-board-header__icon-btn ${showFilters ? 'active' : ''} ${activeFilterCount > 0 ? 'has-badge' : ''}`}
              onClick={() => setShowFilters(!showFilters)}
              aria-label="Filters"
            >
              <FilterOutlined />
              {activeFilterCount > 0 && (
                <span className="task-board-header__badge-dot" />
              )}
            </button>
          </div>

          {/* Bottom row: Scope toggle */}
          <div className="task-board-header__mobile-bottom">
            <button
              className={`task-board-header__scope-toggle ${!showMyTasks ? 'task-board-header__scope-toggle--all' : ''}`}
              onClick={handleScopeToggle}
            >
              <span className={`task-board-header__scope-option ${showMyTasks ? 'active' : ''}`}>
                <UserOutlined /> Mine
              </span>
              <span className={`task-board-header__scope-option ${!showMyTasks ? 'active' : ''}`}>
                <TeamOutlined /> All
              </span>
            </button>

          </div>
        </div>
      ) : (
        /* Desktop Layout */
        <div className="task-board-header__desktop">
          {/* Left: Title + Context */}
          <div className="task-board-header__left">
            <div className="task-board-header__title-row">
              <h1 className="task-board-header__title">
                {showMyTasks ? 'My Tasks' : 'All Tasks'}
              </h1>
              <button
                className="task-board-header__scope-switch"
                onClick={handleScopeToggle}
                aria-label={`Switch to ${showMyTasks ? 'All Tasks' : 'My Tasks'}`}
              >
                {showMyTasks ? <TeamOutlined /> : <UserOutlined />}
              </button>
            </div>
            {contextLine && (
              <span className="task-board-header__context">{contextLine}</span>
            )}
          </div>

          {/* Right: Search + Utilities + Primary Action */}
          <div className="task-board-header__right">
            {/* Search */}
            <div className="task-board-header__search-wrapper">
              <Input
                className="task-board-search"
                placeholder="Search tasks..."
                value={searchValue}
                onChange={(e) => onSearchChange(e.target.value)}
                prefix={<SearchOutlined style={{ color: text.muted }} />}
                allowClear
                onClear={onSearchClear}
              />
            </div>

            {/* Utilities Menu */}
            <Dropdown overlay={utilitiesMenu} trigger={['click']} placement="bottomRight">
              <Tooltip title="Options" placement="bottom">
                <button
                  className={`task-board-header__icon-btn ${showFilters || activeFilterCount > 0 ? 'active' : ''}`}
                  aria-label="Options"
                >
                  <SettingOutlined />
                  {activeFilterCount > 0 && (
                    <span className="task-board-header__badge-dot" />
                  )}
                </button>
              </Tooltip>
            </Dropdown>

            {/* Primary Action */}
            <Button
              type="primary"
              icon={<PlusOutlined />}
              className="task-board-header__primary-btn"
              onClick={onCreateTaskClick}
            >
              New
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

TaskBoardHeader.propTypes = {
  windowWidth: PropTypes.number.isRequired,
  showMyTasks: PropTypes.bool.isRequired,
  setShowMyTasks: PropTypes.func.isRequired,
  searchValue: PropTypes.string,
  onSearchChange: PropTypes.func.isRequired,
  onSearchClear: PropTypes.func.isRequired,
  onBulkImportClick: PropTypes.func.isRequired,
  onCreateTaskClick: PropTypes.func.isRequired,
  level: PropTypes.string,
  setViewChanged: PropTypes.func.isRequired,
  // Filter integration props
  showFilters: PropTypes.bool,
  setShowFilters: PropTypes.func,
  activeFilterCount: PropTypes.number,
  // Context data
  taskCount: PropTypes.number,
  overdueCount: PropTypes.number,
  // Export
  getExportWhere: PropTypes.func,
};

TaskBoardHeader.defaultProps = {
  searchValue: '',
  level: '',
  showFilters: false,
  setShowFilters: () => {},
  activeFilterCount: 0,
  taskCount: 0,
  overdueCount: 0,
  getExportWhere: undefined,
};

export default TaskBoardHeader;
